/**
 * developer-api — catalog + contract for the API Playground.
 *
 * Endpoint files declare metadata only (id, method, path, params, descriptions).
 * Execution is centralized in `executeEndpoint` (see ./helpers.ts) which
 * fetches the displayed URL against the v1 partner REST server. The server
 * owns the supabase query AND the hide-list — what cURL returns is what the
 * playground shows.
 */

// ---------------------------------------------------------------------------
// Internal contract — what an endpoint file must export.
// ---------------------------------------------------------------------------

export type HttpMethod = "GET" | "POST" | "PUT" | "DELETE";

export type ParamType = "uuid" | "string" | "enum";

export type Param = {
  name: string;
  in: "path" | "query";
  required: boolean;
  description: string;
  type?: ParamType;
  /** Choices when `type === "enum"`. */
  options?: string[];
  /** Pre-fill from the current route's `:clientId` when true. */
  defaultFromClientId?: boolean;
};

export type ParamValues = Record<string, string>;

/**
 * What `run()` must return. Mirrors the PostgREST envelope so the playground
 * UI can render status / count / body uniformly across endpoints.
 */
export type RunResult = {
  data: unknown;
  error: unknown;
  status: number;
  statusText: string;
  count: number | null;
};

export type Endpoint = {
  id: string;
  method: HttpMethod;
  /** REST path with `{param}` placeholders, e.g. `/projects/{project_id}`. */
  path: string;
  summary: string;
  description: string;
  params: Param[];
};

export type EndpointGroup = {
  id: string;
  title: string;
  endpoints: Endpoint[];
};

// ---------------------------------------------------------------------------
// Catalog
// ---------------------------------------------------------------------------

import { getProjects } from "./getProjects";
import { listProperties } from "./listProperties";
import { getProperty } from "./getProperty";
import { listProjectAssets } from "./listProjectAssets";
import { listPropertyAssets } from "./listPropertyAssets";
import { getAsset } from "./getAsset";
import { getPropertyAttributes } from "./getPropertyAttributes";

export const GROUPS: EndpointGroup[] = [
  {
    id: "projects",
    title: "Projects",
    endpoints: [getProjects],
  },
  {
    id: "properties",
    title: "Properties",
    endpoints: [listProperties, getProperty],
  },
  {
    id: "assets",
    title: "Assets",
    endpoints: [listProjectAssets, listPropertyAssets, getAsset],
  },
  {
    id: "property-attributes",
    title: "Property attributes",
    endpoints: [getPropertyAttributes],
  },
];

export const ALL_ENDPOINTS: Endpoint[] = GROUPS.flatMap((g) => g.endpoints);
export const ENDPOINT_BY_ID = new Map(ALL_ENDPOINTS.map((e) => [e.id, e]));

export {
  API_BASE_URL,
  buildRequestUrl,
  buildCurl,
  executeEndpoint,
} from "./helpers";
